import React, { useCallback, useEffect, useMemo, useState } from "react";
import useStyles from "./style";
import {
  Box,
  Button,
  Chip,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

import driverApi from "../../../../../Services/Driver";
import {
  createProgramServiceAgreement,
  getContractsByOwnerId,
} from "../../../../../Services/Contracts/programContract";
import { apiUrl } from "../../../../../Constants";

const DEFAULT_ACCOUNT_STATUS = "ONBOARDING";
const DRIVER_ACCOUNT_STATUS_OPTIONS = ["ONBOARDING", "APPROVED", "BANNED"];
const PROGRAM_ACCOUNT_STATUS_OPTIONS = [
  "PENDING",
  "UNDER_REVIEW",
  "APPROVED",
  "REJECTED",
  "CANCELLED",
];
const DRIVER_ACCOUNT_STATUS_LABELS = {
  ONBOARDING: "On Boarding",
  APPROVED: "Approve",
  BANNED: "Banned",
};
const PROGRAM_ACCOUNT_STATUS_LABELS = {
  PENDING: "PENDING",
  UNDER_REVIEW: "UNDER_REVIEW",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  CANCELLED: "CANCELLED",
};
const DRIVER_BLOCKED_ACCOUNT_STATUSES = new Set(["BANNED"]);
const PROGRAM_BLOCKED_ACCOUNT_STATUSES = new Set(["REJECTED", "CANCELLED"]);
const APPROVED_STATUSES = new Set(["approved", "validated", "valid", "accepted"]);
const PENDING_STATUSES = new Set([
  "pending",
  "accessing",
  "in_review",
  "verification_pending",
  "review",
  "processing",
]);
const REJECTED_STATUSES = new Set(["rejected", "declined", "refused", "denied"]);
const EXPIRED_STATUSES = new Set(["expired"]);
const MISSING_STATUSES = new Set([
  "",
  "no_document",
  "not_uploaded",
  "missing",
  "missing_document",
  "not_available",
]);

const normalizeKey = (value) =>
  (value ?? "")
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const normalizeUserType = (value) => {
  const normalized = (value ?? "").toString().trim().toUpperCase();
  if (normalized === "USER") {
    return "USER";
  }

  if (normalized === "PROGRAM") {
    return "PROGRAM";
  }

  return "DRIVER";
};

const normalizeStatusVariant = (value) => (value ?? "").toString().trim().toUpperCase();

const resolveAccountStatusConfig = (statusVariant) =>
  normalizeStatusVariant(statusVariant) === "PROGRAM"
    ? {
        defaultStatus: "PENDING",
        allowedStatuses: PROGRAM_ACCOUNT_STATUS_OPTIONS,
        labels: PROGRAM_ACCOUNT_STATUS_LABELS,
        blockedStatuses: PROGRAM_BLOCKED_ACCOUNT_STATUSES,
        approvalFallbackStatus: "PENDING",
      }
    : {
        defaultStatus: DEFAULT_ACCOUNT_STATUS,
        allowedStatuses: DRIVER_ACCOUNT_STATUS_OPTIONS,
        labels: DRIVER_ACCOUNT_STATUS_LABELS,
        blockedStatuses: DRIVER_BLOCKED_ACCOUNT_STATUSES,
        approvalFallbackStatus: DEFAULT_ACCOUNT_STATUS,
      };

const normalizeAccountStatus = (value, accountStatusConfig) => {
  const normalized = (value ?? "").toString().trim().toUpperCase();
  const allowedStatuses = new Set(accountStatusConfig.allowedStatuses || []);

  if (allowedStatuses.has(normalized)) {
    return normalized;
  }

  if (
    normalized === "APPROVED" ||
    normalized === "APPROVE" ||
    normalized === "VALIDATED" ||
    normalized === "VALID" ||
    normalized === "ACCEPTED"
  ) {
    return allowedStatuses.has("APPROVED")
      ? "APPROVED"
      : accountStatusConfig.approvalFallbackStatus;
  }

  if (
    normalized === "ONBOARDING" ||
    normalized === "PENDING" ||
    normalized === "UNDER_REVIEW" ||
    normalized === "IN_REVIEW" ||
    normalized === "PROCESSING" ||
    normalized === "REVIEWING"
  ) {
    if (allowedStatuses.has("PENDING")) {
      return "PENDING";
    }

    if (allowedStatuses.has("UNDER_REVIEW")) {
      return "UNDER_REVIEW";
    }

    return allowedStatuses.has("ONBOARDING")
      ? "ONBOARDING"
      : accountStatusConfig.approvalFallbackStatus;
  }

  if (
    normalized === "BANNED" ||
    normalized === "REJECTED" ||
    normalized === "DECLINED" ||
    normalized === "DENIED" ||
    normalized === "REFUSED" ||
    normalized === "CANCELLED" ||
    normalized === "CANCELED"
  ) {
    if (allowedStatuses.has("REJECTED")) {
      return "REJECTED";
    }

    if (allowedStatuses.has("CANCELLED")) {
      return "CANCELLED";
    }

    if (allowedStatuses.has("BANNED")) {
      return "BANNED";
    }
  }

  return accountStatusConfig.approvalFallbackStatus;
};

const isBlockedAccountStatus = (value, accountStatusConfig) =>
  (accountStatusConfig?.blockedStatuses || new Set()).has(
    (value ?? "").toString().trim().toUpperCase()
  );

const resolveMediaUrl = (value) => {
  if (!value) {
    return "";
  }

  const stringValue = value.toString();
  if (
    stringValue.startsWith("http://") ||
    stringValue.startsWith("https://") ||
    stringValue.startsWith("data:") ||
    stringValue.startsWith("blob:")
  ) {
    return stringValue;
  }

  return `${apiUrl}${stringValue}`;
};

const normalizeDocumentDefinitions = (definitions = []) =>
  (Array.isArray(definitions) ? definitions : [])
    .map((definition, index) => {
      if (!definition || typeof definition !== "object") {
        return null;
      }

      const documentType =
        definition?.documentType ||
        definition?.name ||
        definition?.documentTypeAlias ||
        "";

      return {
        ...definition,
        id: definition?.id || definition?._id || index + 1,
        documentType,
        documentTypeAlias:
          definition?.documentTypeAlias ||
          definition?.alias ||
          normalizeKey(documentType),
      };
    })
    .filter(Boolean);

const findMatchingDocument = (definition, uploadedDocuments = []) => {
  const normalizedDefinition = normalizeKey(
    definition?.documentTypeAlias || definition?.documentType
  );

  return (Array.isArray(uploadedDocuments) ? uploadedDocuments : []).find((item) => {
    const candidates = [
      item?.documentTypeAlias,
      item?.documentType,
      item?.document_type,
      item?.documentDescription,
      item?.alias,
      item?.type,
      item?.name,
    ];

    return candidates.some(
      (candidate) => normalizeKey(candidate) === normalizedDefinition
    );
  });
};

const getDocumentMeta = (row) => {
  const rawStatus = row?.rawStatus || row?.status || "";
  const statusKey = normalizeKey(rawStatus);
  const hasFile = Boolean(row?.file);

  if (!hasFile || MISSING_STATUSES.has(statusKey)) {
    return {
      state: "missing",
      label: "Non chargé",
      tone: "error",
      detail: "Aucun document téléchargé",
      actionLabel: "Charger",
      actionType: "upload",
      actionIcon: <UploadFileOutlinedIcon fontSize="small" />,
    };
  }

  if (APPROVED_STATUSES.has(statusKey)) {
    return {
      state: "approved",
      label: "Validé",
      tone: "success",
      detail: "Document valide",
      actionLabel: "Voir",
      actionType: "view",
      actionIcon: <VisibilityOutlinedIcon fontSize="small" />,
    };
  }

  if (PENDING_STATUSES.has(statusKey)) {
    return {
      state: "pending",
      label: "En attente",
      tone: "warning",
      detail: "En cours de validation par l'administrateur",
      actionLabel: "Voir",
      actionType: "view",
      actionIcon: <VisibilityOutlinedIcon fontSize="small" />,
    };
  }

  if (EXPIRED_STATUSES.has(statusKey)) {
    return {
      state: "rejected",
      label: "Expiré",
      tone: "error",
      detail: "Le document est expiré",
      actionLabel: "Voir",
      actionType: "view",
      actionIcon: <VisibilityOutlinedIcon fontSize="small" />,
    };
  }

  if (REJECTED_STATUSES.has(statusKey)) {
    return {
      state: "rejected",
      label: "Rejeté",
      tone: "error",
      detail: "Le document a été rejeté",
      actionLabel: "Voir",
      actionType: "view",
      actionIcon: <VisibilityOutlinedIcon fontSize="small" />,
    };
  }

  return {
    state: "pending",
    label: hasFile ? "En attente" : "Non chargé",
    tone: hasFile ? "warning" : "error",
    detail: hasFile
      ? "En cours de validation par l'administrateur"
      : "Aucun document téléchargé",
    actionLabel: hasFile ? "Voir" : "Charger",
    actionType: hasFile ? "view" : "upload",
    actionIcon: hasFile ? (
      <VisibilityOutlinedIcon fontSize="small" />
    ) : (
      <UploadFileOutlinedIcon fontSize="small" />
    ),
  };
};

const Status = ({
  onRef,
  driverId,
  openDialog,
  openDocumentsTab,
  setsubmitButton,
  setSkipButton,
  userType = "DRIVER",
  documentsUserType,
  statusVariant = "DEFAULT",
  entityLabel = "Driver",
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const resolvedUserType = normalizeUserType(userType);
  const resolvedDocumentsUserType = normalizeUserType(documentsUserType || userType);
  const accountStatusConfig = useMemo(
    () => resolveAccountStatusConfig(statusVariant),
    [statusVariant]
  );
  const showDriverStatusToggle = normalizeStatusVariant(statusVariant) !== "PROGRAM";
  const entityLabelLower = entityLabel.toLowerCase();
  const accountStatusOptions = useMemo(
    () =>
      (accountStatusConfig.allowedStatuses || []).map((status) => ({
        value: status,
        label: accountStatusConfig.labels?.[status] || status,
      })),
    [accountStatusConfig]
  );

  const [isEditable, setIsEditable] = useState(false);
  const [accountStatus, setAccountStatus] = useState(accountStatusConfig.defaultStatus);
  const [driverStatus, setDriverStatus] = useState(false);
  const [documentDefinitions, setDocumentDefinitions] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    setsubmitButton(true);
    setSkipButton(true);
  }, [setsubmitButton, setSkipButton]);

  const refreshStatusData = useCallback(async () => {
    if (!driverId) {
      setDocumentDefinitions([]);
      setUploadedDocuments([]);
      setAccountStatus(accountStatusConfig.defaultStatus);
      setDriverStatus(false);
      setHasLoaded(true);
      return;
    }

    setHasLoaded(false);
    setDocumentDefinitions([]);
    setUploadedDocuments([]);
    setAccountStatus(accountStatusConfig.defaultStatus);
    setDriverStatus(false);
    dispatch(showLoader("Loading please wait..."));

    try {
      const [completeDetailsResult, documentTypesResult] = await Promise.allSettled([
        driverApi.getDriverCompleteDetailsByDId(driverId, {
          userType: resolvedUserType,
        }),
        driverApi.getDocumentsTypes(resolvedDocumentsUserType),
      ]);

      if (completeDetailsResult.status === "fulfilled") {
        const detailsPayload = completeDetailsResult.value?.data?.data || {};
        const programDetails = detailsPayload?.driverprogramInfo || {};
        const serviceDetails = detailsPayload?.driverServiceDetails || {};
        const rawAccountStatus =
          normalizeStatusVariant(statusVariant) === "PROGRAM"
            ? programDetails?.accountStatus ||
              serviceDetails?.accountStatus ||
              detailsPayload?.accountStatus ||
              detailsPayload?.status
            : serviceDetails?.accountStatus ||
              detailsPayload?.accountStatus ||
              detailsPayload?.status;
        const nextAccountStatus = normalizeAccountStatus(rawAccountStatus, accountStatusConfig);
        setAccountStatus(nextAccountStatus);
        setDriverStatus(
          isBlockedAccountStatus(nextAccountStatus, accountStatusConfig)
            ? false
            : serviceDetails?.driverAccountStatus === "ACTIVE"
        );
        setUploadedDocuments(
          Array.isArray(detailsPayload?.driverDocumentsDetails)
            ? detailsPayload.driverDocumentsDetails
            : []
        );
      } else {
        dispatch(
          showSnackbar({
            message: `Failed to fetch ${entityLabelLower} details`,
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          })
        );
      }

      if (documentTypesResult.status === "fulfilled") {
        const rawTypes = documentTypesResult.value?.data?.data;
        const normalizedTypes = Array.isArray(rawTypes)
          ? rawTypes
          : Array.isArray(rawTypes?.data)
          ? rawTypes.data
          : [];

        setDocumentDefinitions(normalizeDocumentDefinitions(normalizedTypes));
      } else {
        dispatch(
          showSnackbar({
            message: "Failed to fetch document types",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          })
        );
        setDocumentDefinitions([]);
      }
    } finally {
      dispatch(hideLoader());
      setHasLoaded(true);
    }
  }, [
    accountStatusConfig,
    dispatch,
    driverId,
    entityLabelLower,
    resolvedDocumentsUserType,
    resolvedUserType,
    statusVariant,
  ]);

  useEffect(() => {
    if (openDialog === false) {
      return;
    }

    refreshStatusData();
  }, [openDialog, refreshStatusData]);

  const documentRows = useMemo(() => {
    const sourceDefinitions =
      documentDefinitions.length > 0
        ? documentDefinitions
        : uploadedDocuments.map((document, index) => ({
            id: document?.id || document?.documentId || index + 1,
            documentType:
              document?.documentType ||
              document?.name ||
              document?.documentTypeAlias ||
              `Document ${index + 1}`,
            documentTypeAlias:
              document?.documentTypeAlias ||
              normalizeKey(
                document?.documentType ||
                  document?.name ||
                  document?.documentTypeAlias ||
                  `Document ${index + 1}`
              ),
          }));

    return sourceDefinitions.map((definition, index) => {
      const matchedDocument = findMatchingDocument(definition, uploadedDocuments);
      const rawDocumentPath =
        matchedDocument?.documentPath ||
        matchedDocument?.file ||
        matchedDocument?.documentUrl ||
        matchedDocument?.document ||
        matchedDocument?.path ||
        matchedDocument?.filePath ||
        "";

      const file = resolveMediaUrl(rawDocumentPath);
      const rowData = {
        id: definition?.id || matchedDocument?.id || matchedDocument?.documentId || index + 1,
        documentId:
          matchedDocument?.id || matchedDocument?.documentId || definition?.id || index + 1,
        documentType: definition?.documentType || definition?.name || "Document",
        documentTypeAlias:
          definition?.documentTypeAlias ||
          normalizeKey(definition?.documentType || definition?.name || `Document ${index + 1}`),
        file,
        fileName:
          matchedDocument?.fileName ||
          matchedDocument?.uploadedDocumentName ||
          definition?.documentType ||
          "Document",
        rawStatus: matchedDocument?.status || matchedDocument?.documentStatus || "",
      };

      const statusMeta = getDocumentMeta(rowData);

      return {
        ...rowData,
        statusMeta,
        statusLabel: statusMeta.label,
        detail: statusMeta.detail,
        actionType: statusMeta.actionType,
      };
    });
  }, [documentDefinitions, uploadedDocuments]);

  const documentStats = useMemo(() => {
    const totals = documentRows.reduce(
      (acc, row) => {
        if (row.statusMeta?.state === "approved") {
          acc.validated += 1;
        } else if (row.statusMeta?.state === "pending") {
          acc.pending += 1;
        } else if (row.statusMeta?.state === "rejected") {
          acc.rejected += 1;
        } else {
          acc.missing += 1;
        }

        return acc;
      },
      {
        total: documentRows.length,
        validated: 0,
        pending: 0,
        rejected: 0,
        missing: 0,
      }
    );

    const rejectedMissing = totals.rejected + totals.missing;
    const progress = totals.total > 0 ? Math.round((totals.validated / totals.total) * 100) : 0;

    return {
      ...totals,
      rejectedMissing,
      progress,
    };
  }, [documentRows]);

  const isFullyValidated =
    documentStats.total > 0 && documentStats.validated === documentStats.total;
  const documentAttentionCount = documentStats.pending + documentStats.rejectedMissing;
  const isAccountBlocked = isBlockedAccountStatus(accountStatus, accountStatusConfig);

  const ensureProgramContractExists = useCallback(async () => {
    if (!driverId) {
      return;
    }

    try {
      const contractsByOwnerId = await getContractsByOwnerId();
      if (contractsByOwnerId[driverId]) {
        return;
      }

      await createProgramServiceAgreement(driverId);
    } catch (error) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.message ||
            "Failed to create the associated service contract",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    }
  }, [dispatch, driverId]);

  const persistStatusChange = useCallback(
    async (nextAccountStatus, nextDriverStatus) => {
      if (!driverId) {
        return false;
      }

      dispatch(showLoader("Loading please wait..."));

      try {
        const response =
          normalizeStatusVariant(statusVariant) === "PROGRAM"
            ? await driverApi.updateDriverProgramStatus(driverId, {
                programStatus: nextAccountStatus,
              })
            : await driverApi.updateDriverServiceStatus(driverId, {
                accountStatus: nextAccountStatus,
                driverAccountStatus:
                  isBlockedAccountStatus(nextAccountStatus, accountStatusConfig)
                    ? "DE_ACTIVE"
                    : nextDriverStatus
                    ? "ACTIVE"
                    : "DE_ACTIVE",
                userType: resolvedUserType,
              });

        if (response?.status === 200 || response?.status === 201) {
          dispatch(
            showSnackbar({
              message: `${entityLabel} status updated successfully`,
              autoHideDuration: 3000,
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              variant: "success",
            })
          );

          if (normalizeStatusVariant(statusVariant) === "PROGRAM" && nextAccountStatus === "APPROVED") {
            ensureProgramContractExists();
          }

          return true;
        }

        throw new Error("Unexpected status update response");
      } catch (error) {
        dispatch(
          showSnackbar({
            message: error?.response?.data?.message || `Error while update the ${entityLabelLower} status`,
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          })
        );
        return false;
      } finally {
        dispatch(hideLoader());
      }
    },
    [
      accountStatusConfig,
      dispatch,
      driverId,
      ensureProgramContractExists,
      entityLabel,
      entityLabelLower,
      resolvedUserType,
      statusVariant,
    ]
  );

  const handleAccountStatusChange = async (event) => {
    if (!isEditable) {
      return;
    }

    const nextAccountStatus = event.target.value;
    if (!accountStatusOptions.some((option) => option.value === nextAccountStatus)) {
      return;
    }
    const previousAccountStatus = accountStatus;
    const previousDriverStatus = driverStatus;
    const nextDriverStatus =
      isBlockedAccountStatus(nextAccountStatus, accountStatusConfig)
        ? false
        : previousDriverStatus;

    setAccountStatus(nextAccountStatus);
    setDriverStatus(nextDriverStatus);

    const success = await persistStatusChange(nextAccountStatus, nextDriverStatus);
    if (!success) {
      setAccountStatus(previousAccountStatus);
      setDriverStatus(previousDriverStatus);
    }
  };

  const handleDriverStatusToggle = async () => {
    if (!isEditable || isAccountBlocked) {
      return;
    }

    const nextDriverStatus = !driverStatus;
    const previousAccountStatus = accountStatus;
    const previousDriverStatus = driverStatus;

    setDriverStatus(nextDriverStatus);

    const success = await persistStatusChange(accountStatus, nextDriverStatus);
    if (!success) {
      setAccountStatus(previousAccountStatus);
      setDriverStatus(previousDriverStatus);
    }
  };

  const handleApprovalToggle = useCallback(
    async (event) => {
      if (!isEditable || isAccountBlocked) {
        return;
      }

      const nextAccountStatus = event.target.checked
        ? "APPROVED"
        : accountStatusConfig.approvalFallbackStatus;
      if (!accountStatusOptions.some((option) => option.value === nextAccountStatus)) {
        return;
      }
      if (nextAccountStatus === accountStatus) {
        return;
      }

      const previousAccountStatus = accountStatus;
      const previousDriverStatus = driverStatus;

      setAccountStatus(nextAccountStatus);

      const success = await persistStatusChange(nextAccountStatus, previousDriverStatus);
      if (!success) {
        setAccountStatus(previousAccountStatus);
        setDriverStatus(previousDriverStatus);
      }
    },
    [
      accountStatus,
      accountStatusConfig,
      accountStatusOptions,
      driverStatus,
      isAccountBlocked,
      isEditable,
      persistStatusChange,
    ]
  );

  const handleOpenDocument = useCallback((row) => {
    const url = resolveMediaUrl(row?.file);
    if (!url) {
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const handleOpenDocumentsTab = useCallback(() => {
    if (typeof openDocumentsTab === "function") {
      openDocumentsTab();
    }
  }, [openDocumentsTab]);

  const handleDocumentAction = useCallback((row) => {
    if (row?.actionType === "upload") {
      if (typeof openDocumentsTab === "function") {
        openDocumentsTab();
      }
      return;
    }

    handleOpenDocument(row);
  }, [handleOpenDocument, openDocumentsTab]);

  const getFormData = useCallback(
    () => ({
      driverStatus,
      accountStatus,
      documentStats,
      documentRows,
    }),
    [accountStatus, documentRows, documentStats, driverStatus]
  );

  useEffect(() => {
    if (typeof onRef === "function") {
      onRef(getFormData);
    }
  }, [getFormData, onRef]);

  const documentColumns = useMemo(
    () => [
      {
        field: "documentType",
        headerName: "Document",
        flex: 1.35,
        minWidth: 260,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params?.row || {};
          const showAlias =
            normalizeKey(row?.documentTypeAlias) !== normalizeKey(row?.documentType);

          return (
            <Box className={classes.documentCell}>
              <Box className={classes.documentIcon}>
                <DescriptionOutlinedIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" className={classes.documentName} noWrap>
                  {row?.documentType}
                </Typography>
                {showAlias && (
                  <Typography variant="caption" className={classes.documentAlias} noWrap>
                    {row?.documentTypeAlias}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.8,
        minWidth: 160,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const row = params?.row || {};
          const meta = row?.statusMeta || {};
          const toneColors = {
            success: { backgroundColor: "#e8f5e9", color: "#11823b" },
            warning: { backgroundColor: "#fff4e5", color: "#fb8c00" },
            error: { backgroundColor: "#fdecef", color: "#e53935" },
          };

          return (
            <Chip
              label={meta?.label || row?.statusLabel || "Pending"}
              size="small"
              sx={{
                height: 28,
                fontWeight: 800,
                borderRadius: 999,
                ...toneColors[meta?.tone || "warning"],
                "& .MuiChip-label": {
                  px: 1.5,
                },
              }}
            />
          );
        },
      },
      {
        field: "details",
        headerName: "Details",
        flex: 1.45,
        minWidth: 260,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Typography variant="body2" className={classes.detailText}>
            {params?.row?.detail}
          </Typography>
        ),
      },
      {
        field: "action",
        headerName: "Actions",
        flex: 0.75,
        minWidth: 150,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const row = params?.row || {};

          return (
            <Button
              variant="text"
              color="success"
              startIcon={row?.statusMeta?.actionIcon}
              onClick={() => handleDocumentAction(row)}
              className={classes.rowActionButton}
              size="small"
            >
              {row?.statusMeta?.actionLabel || "Voir"}
            </Button>
          );
        },
      },
    ],
    [classes, handleDocumentAction]
  );

  if (!hasLoaded) {
    return (
      <Box className={classes.loadingState}>
        <Typography variant="body2">Loading status...</Typography>
      </Box>
    );
  }

  const progressColor = isFullyValidated ? "#11823b" : "#dc2626";

  return (
    <Box className={classes.root}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" className={classes.editRow}>
        <FormControlLabel
          control={
            <Switch
              checked={isEditable}
              onChange={(event) => setIsEditable(event.target.checked)}
              color="success"
            />
          }
          label={<Typography className={classes.toggleLabel}>Enable Edit</Typography>}
        />
      </Stack>

      <Typography variant="body2" className={classes.noteText}>
        Note: Manually updating the {entityLabelLower} status will reflect the corresponding changes in the app flow.
      </Typography>

      <Paper variant="outlined" className={classes.statusPanel}>
        <Box className={!isEditable ? classes.statusControlsLocked : ""}>
          <Box className={classes.statusHeaderRow}>
            <Typography variant="body1" className={classes.fieldLabel}>
              Account Status
            </Typography>
            <FormControl disabled={!isEditable}>
              <RadioGroup
                row
                value={accountStatus}
                onChange={handleAccountStatusChange}
                className={classes.radioGroup}
              >
                {accountStatusOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio color="success" />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>

          <Divider className={classes.sectionDivider} />

          {showDriverStatusToggle && (
            <Box className={classes.statusToggleRow}>
              <Typography variant="body1" className={classes.fieldLabel}>
                {entityLabel} Status (Active/De-active)
              </Typography>
              <Switch
                checked={driverStatus}
                onChange={handleDriverStatusToggle}
                color="success"
                disabled={isAccountBlocked}
                className={classes.statusSwitch}
              />
            </Box>
          )}
        </Box>
      </Paper>

      <Paper variant="outlined" className={classes.verificationPanel}>
        <Box className={classes.verificationHeader}>
          <Box>
            <Typography variant="h6" className={classes.verificationTitle}>
              VÉRIFICATION DES DOCUMENTS
            </Typography>
            <Typography variant="body2" className={classes.verificationSubtitle}>
              Vérifiez si tous les documents requis ont été chargés et validés.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            color="success"
            startIcon={<DescriptionOutlinedIcon />}
            onClick={refreshStatusData}
            className={classes.verifyBtn}
          >
            Vérifier les documents
          </Button>
        </Box>

        <Box className={classes.metricsGrid}>
          <Paper variant="outlined" className={classes.metricCard}>
            <Box className={classes.metricIcon} sx={{ backgroundColor: "#edf4ff", color: "#2563eb" }}>
              <DescriptionOutlinedIcon fontSize="small" />
            </Box>
            <Box className={classes.metricContent}>
              <Typography className={classes.metricLabel}>Documents requis</Typography>
              <Typography className={classes.metricValue}>{documentStats.total}</Typography>
              <Typography className={classes.metricHelper}>Pièces attendues</Typography>
            </Box>
          </Paper>

          <Paper variant="outlined" className={classes.metricCard}>
            <Box className={classes.metricIcon} sx={{ backgroundColor: "#e8f5e9", color: "#11823b" }}>
              <CheckCircleOutlineOutlinedIcon fontSize="small" />
            </Box>
            <Box className={classes.metricContent}>
              <Typography className={classes.metricLabel}>Validés</Typography>
              <Typography className={classes.metricValue}>{documentStats.validated}</Typography>
              <Typography className={classes.metricHelper}>Documents approuvés</Typography>
            </Box>
          </Paper>

          <Paper variant="outlined" className={classes.metricCard}>
            <Box className={classes.metricIcon} sx={{ backgroundColor: "#fff4e5", color: "#fb8c00" }}>
              <PendingOutlinedIcon fontSize="small" />
            </Box>
            <Box className={classes.metricContent}>
              <Typography className={classes.metricLabel}>En attente</Typography>
              <Typography className={classes.metricValue}>{documentStats.pending}</Typography>
              <Typography className={classes.metricHelper}>En cours de revue</Typography>
            </Box>
          </Paper>

          <Paper variant="outlined" className={classes.metricCard}>
            <Box className={classes.metricIcon} sx={{ backgroundColor: "#fdecef", color: "#e53935" }}>
              <ErrorOutlineOutlinedIcon fontSize="small" />
            </Box>
            <Box className={classes.metricContent}>
              <Typography className={classes.metricLabel}>Rejetés / Manquants</Typography>
              <Typography className={classes.metricValue}>{documentStats.rejectedMissing}</Typography>
              <Typography className={classes.metricHelper}>
                {documentStats.rejected} rejetés, {documentStats.missing} manquants
              </Typography>
            </Box>
          </Paper>

          <Paper variant="outlined" className={classes.metricCard}>
            <Box className={classes.metricIcon} sx={{ backgroundColor: "#eef2ff", color: progressColor }}>
              <CheckCircleOutlineOutlinedIcon fontSize="small" />
            </Box>
            <Box className={classes.progressWrap}>
              <Box className={classes.progressTop}>
                <Typography className={classes.metricLabel}>Progression</Typography>
                <Typography className={classes.metricValue} sx={{ color: progressColor, fontSize: 20 }}>
                  {documentStats.progress}%
                </Typography>
              </Box>
              <Box className={classes.progressBar}>
                <Box
                  sx={{
                    height: "100%",
                    width: `${documentStats.progress}%`,
                    borderRadius: 999,
                    backgroundColor: progressColor,
                    transition: "width 200ms ease",
                  }}
                />
              </Box>
              <Typography className={classes.metricHelper}>
                {documentStats.validated}/{documentStats.total} validés
              </Typography>
            </Box>
          </Paper>
        </Box>

        {isFullyValidated ? (
          <Box className={classes.successBanner}>
            <Box className={classes.successBannerHeader}>
              <Box className={classes.successIconWrap}>
                <CheckCircleOutlineOutlinedIcon sx={{ color: "#11823b", fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="body2" className={classes.successTitle}>
                  Félicitations !
                </Typography>
                <Typography variant="body2" className={classes.successSubtitle}>
                  Tous les documents requis ont été chargés et validés avec succès.
                  
                </Typography>
              </Box>
            </Box>

            <Box className={classes.successActions}>
              <Button
                variant="outlined"
                color="success"
                disabled={typeof openDocumentsTab !== "function"}
                onClick={handleOpenDocumentsTab}
                startIcon={<DescriptionOutlinedIcon />}
              >
                Voir les documents
              </Button>
                <FormControlLabel
                  className={classes.approvalToggle}
                  control={
                    <Checkbox
                      color="success"
                      checked={accountStatus === "APPROVED"}
                      onChange={handleApprovalToggle}
                      disabled={!isEditable || isAccountBlocked}
                    />
                  }
                  label="On approuve"
                />
            </Box>
          </Box>
        ) : (
          <Box className={classes.warningBanner}>
            <Box className={classes.warningBannerHeader}>
              <Box className={classes.warningIconWrap}>
                <WarningAmberOutlinedIcon sx={{ color: "#b42318", fontSize: 18 }} />
              </Box>
              <Typography variant="body2" className={classes.warningTitle}>
                {documentAttentionCount} documents necessitent une action
              </Typography>
            </Box>
            <Typography variant="body2" className={classes.warningSubtitle}>
              Tous les documents obligatoires doivent être chargés et validés.
            </Typography>
          </Box>
        )}

        <Box className={classes.tableWrapper}>
          {documentRows.length > 0 ? (
            <DataGrid
              rows={documentRows}
              columns={documentColumns}
              autoHeight
              disableSelectionOnClick
              hideFooter
              rowHeight={68}
              getRowId={(row) => row?.id}
              className={classes.dataGridTable}
            />
          ) : (
            <Box className={classes.emptyState}>
              <Typography variant="body2">Aucun document requis trouvé pour ce {entityLabelLower}.</Typography>
            </Box>
          )}
        </Box>

        {isFullyValidated && (
          <Box className={classes.infoBanner}>
            <Box className={classes.infoIconWrap}>
              <InfoOutlinedIcon sx={{ color: "#2563eb", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="body2" className={classes.infoTitle}>
                Information
              </Typography>
              <Typography variant="body2" className={classes.infoSubtitle}>
                Tous les documents sont à jour et validés.
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Status;
